import React, { useState, useRef, useEffect } from "react"
import ReactDOM from "react-dom"

const Tooltip = React.memo(function Tooltip({ children, lines }) {
  const [visible, setVisible] = useState(false)
  const [pos, setPos]         = useState({ x: 0, y: 0, shift: 0, placement: "top" })
  const anchorRef             = useRef(null)
  const tooltipRef            = useRef(null)

  const updatePos = () => {
    if (!anchorRef.current) return
    const r = anchorRef.current.getBoundingClientRect()
    const x      = r.left + r.width / 2
    const yTop   = r.top        // viewport-relative — for "top" placement
    const yBottom = r.bottom    // viewport-relative — for "bottom" placement
    setPos({ x, yTop, yBottom, shift: 0, placement: "top" })
  }

  // After tooltip mounts:
  // 1. Check vertical space — flip to bottom if not enough room on top
  // 2. Check horizontal overflow and shift accordingly
  useEffect(() => {
    if (!visible || !tooltipRef.current || !anchorRef.current) return

    const t      = tooltipRef.current.getBoundingClientRect()
    const r      = anchorRef.current.getBoundingClientRect()
    const vw     = window.innerWidth
    const MARGIN = 10
    const GAP    = 10

    // Determine placement
    const spaceAbove = r.top
    const tooltipH   = t.height
    const placement  = spaceAbove < tooltipH + GAP ? "bottom" : "top"

    // Horizontal shift
    let shift = 0
    if (t.right > vw - MARGIN) shift = (vw - MARGIN) - t.right
    if (t.left  < MARGIN)      shift = MARGIN - t.left

    setPos(p => ({ ...p, shift, placement }))
  }, [visible])

  const handleEnter = () => { updatePos(); setVisible(true) }
  const handleLeave = () => setVisible(false)

  const isBottom = pos.placement === "bottom"

  const tooltip = visible ? ReactDOM.createPortal(
    <div
      ref={tooltipRef}
      className={`gs-tooltip gs-tooltip--portal gs-tooltip--${pos.placement}`}
      style={{
        position:  "fixed",
        left:      pos.x + pos.shift,
        top:       isBottom
          ? pos.yBottom + 10
          : pos.yTop - 10,
        transform: isBottom
          ? "translateX(-50%)"
          : "translateX(-50%) translateY(-100%)",
      }}
    >
      {isBottom && (
        <div
          className="gs-tooltip-arrow gs-tooltip-arrow--top"
          style={{ left: `calc(50% - ${pos.shift}px)` }}
        />
      )}
      {lines.map((line, i) => (
        <div key={i} className={`gs-tooltip-line ${line.type || ""}`}>
          {line.icon && <span className="gs-tooltip-icon">{line.icon}</span>}
          <span>{line.text}</span>
        </div>
      ))}
      {!isBottom && (
        <div
          className="gs-tooltip-arrow"
          style={{ left: `calc(50% - ${pos.shift}px)` }}
        />
      )}
    </div>,
    document.body
  ) : null

  return (
    <div
      ref={anchorRef}
      className="gs-tooltip-anchor"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {children}
      {tooltip}
    </div>
  )
})

export default Tooltip